import React, { PureComponent, createElement } from 'react';

import { hoist, getDisplayName } from '@lglp/components';
import { curry, has, omit } from 'ramda';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';

import { globalError } from '../../actions/errors';
import { getLang } from '../../helpers/i18n';
import { getGlobalError } from '../../selectors/errors';

export const genInitialProps =
  (Component, newProps = {}) =>
  async (data, props) =>
    Component.getInitialProps
      ? Component.getInitialProps(data, { ...props, ...newProps })
      : data;

/**
 * @deprecated - use useTranslation hook or withTranslation HOC from 'react-i18next'
 */
export const translate = (ComposedComponent) => {
  const Translate = (props) => {
    const { t, i18n } = useTranslation();
    const lang = getLang(i18n.language);

    return ComposedComponent ? (
      <ComposedComponent {...props} t={t} lang={lang} />
    ) : null;
  };

  Translate.displayName = `translate(${getDisplayName(ComposedComponent)})`;

  return hoist(Translate, ComposedComponent);
};

export const withReload = curry((reload, ComposedComponent) =>
  hoist(
    class extends PureComponent {
      static displayName = `withReload(${getDisplayName(ComposedComponent)})`;

      componentDidMount() {
        reload(this.props);
      }

      componentDidUpdate(prevProps) {
        reload(this.props, prevProps);
      }

      render() {
        return createElement(
          ComposedComponent,
          { ...this.props, children: null },
          this.props.children
        );
      }
    },
    ComposedComponent
  )
);

const errorEnhancer = connect(
  (state, { error: pError }) => ({
    error: pError || getGlobalError(state),
  }),
  {
    handleError: globalError,
  }
);

export const withErrorHandling = (Nested) => {
  class ErrorHandler extends PureComponent {
    static displayName = `withErrorHandling(${getDisplayName(Nested)})`;

    static getInitialProps = async (data, ...args) => {
      try {
        return await genInitialProps(Nested)(data, ...args);
      } catch (e) {
        /* istanbul ignore next */
        if (process.env.RAZZLE_ENV !== 'prod') {
          console.log('Something wrong during getInitialProps!'); // eslint-disable-line
          console.log(e); // eslint-disable-line
        }
        data.store.dispatch(globalError(e));
        return data;
      }
    };

    componentDidCatch = (error) => {
      this.props.handleError(error);
    };

    render() {
      return createElement(
        Nested,
        { ...omit(['handleError'], this.props), children: null },
        this.props.children
      );
    }
  }

  return errorEnhancer(ErrorHandler);
};

export const filterNoProp = curry(
  (name, Nested) =>
    class extends PureComponent {
      static displayName = `FilterNo:${name}`;

      static getInitialProps = genInitialProps(Nested);

      render() {
        if (has(name, this.props)) {
          return createElement(
            Nested,
            { ...this.props, children: null },
            this.props.children
          );
        }

        return null;
      }
    }
);
